import React, { useEffect, useState } from 'react';
import { getLocalVerificationUrl } from '../../constants';
import { useGetBadgeMediaQuery } from '../../slices/credentialingApi';
import LinkedInShareButton from './LinkedInShareButton';
import util from '../../styles/utilities.module.css';
import './BadgeDetailsModal.css';

const getBadgeIcon = (badgeType) => {
  switch (badgeType) {
    case 'fluency':
    case 'engagement':
    case 'impact':
      return '⭐';
    default:
      return '🎖️';
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const BadgeDetailsModal = ({
  badge,
  digitalBadge,
  isOpen,
  isLoading = false,
  onClose,
  onLinkedInShare,
}) => {
  const badgeId = badge?.badge_id ?? badge?.id;
  const { data: mediaData } = useGetBadgeMediaQuery(badgeId, {
    skip: !badgeId,
  });

  const media = mediaData?.data || mediaData || {};
  const imageUrl =
    media.image_url ||
    media.large_url ||
    media.image ||
    media.hero_url ||
    media.icon_url ||
    media.icon ||
    digitalBadge?.image_url ||
    badge?.image_url ||
    null;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    const modal = document.querySelector('.badge-details-modal');
    if (modal) {
      const closeButton = modal.querySelector('.badge-details-modal__close');
      closeButton?.focus();
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const rawVerificationUrl = digitalBadge
    ? digitalBadge.verification_url ||
      (digitalBadge.credential_id
        ? `http://localhost:8000/api/badges/verify/${digitalBadge.credential_id}/`
        : null)
    : null;

  const verificationUrl = rawVerificationUrl
    ? getLocalVerificationUrl(rawVerificationUrl)
    : null;

  const shareText = verificationUrl
    ? `I just earned the "${badge?.name}" badge from ClassGenius! Verify it at: ${verificationUrl}`
    : '';
  const [facebookTooltipVisible, setFacebookTooltipVisible] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setFacebookTooltipVisible(false);
    }
  }, [isOpen]);

  if (!isOpen || (!badge && !isLoading)) {
    return null;
  }

  const copyShareTextToClipboard = (text) => {
    if (
      !text ||
      typeof window === 'undefined' ||
      typeof document === 'undefined' ||
      !document.body
    ) {
      return Promise.resolve(false);
    }

    const fallbackCopy = () => {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.setAttribute('readonly', '');
      textArea.style.position = 'absolute';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);

      const selection = document.getSelection();
      const selectedRange = selection?.rangeCount ? selection.getRangeAt(0) : null;
      textArea.select();

      let succeeded = false;
      try {
        succeeded = document.execCommand('copy');
      } catch (error) {
        console.warn('[BadgeDetailsModal] Fallback clipboard copy failed', error);
      } finally {
        if (selectedRange && selection) {
          selection.removeAllRanges();
          selection.addRange(selectedRange);
        }
        document.body.removeChild(textArea);
      }

      return succeeded;
    };

    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      return navigator.clipboard.writeText(text).then(
        () => true,
        (error) => {
          console.warn('[BadgeDetailsModal] Clipboard API copy failed, trying fallback', error);
          return fallbackCopy();
        }
      );
    }

    return Promise.resolve(fallbackCopy());
  };

  const handleSocialShare = (platform) => {
    if (!verificationUrl) {
      return;
    }

    let socialUrl;
    let clipboardPromise = null;
    switch (platform) {
      case 'twitter':
        return `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
      case 'facebook':
        return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          verificationUrl
        )}&quote=${encodeURIComponent(shareText)}`;
        clipboardPromise = copyShareTextToClipboard(shareText);
        setFacebookTooltipVisible(true);
        break;
      case 'email':
        // Use Gmail web compose - works in browser without email client
        return `https://mail.google.com/mail/?view=cm&su=${encodeURIComponent(
          `Check out my ${badge?.name} badge!`
        )}&body=${encodeURIComponent(shareText)}`;
      default:
        return;
    }

    if (platform === 'email') {
      window.location.href = socialUrl;
      return;
    }

    window.open(socialUrl, '_blank', 'width=600,height=400');

    clipboardPromise?.catch((error) => {
      console.warn('[BadgeDetailsModal] Clipboard copy promise rejected', error);
    });
  };

  const linkedinButtonClass = `${util.btn}  ${util.elevateHover} ${util['btn--linkedin']}`;
  const socialButtonClass = `${util.btn} ${util['btn-sm']} ${util['btn--ghost']} ${util.elevateHover}`;
  const facebookShareTooltip = 'Copies the message—paste it into Facebook when the window opens.';
  const facebookTooltipId = 'facebook-share-tooltip';
  const earnedDate = digitalBadge?.issued_date || badge?.earned_at;

  return (
    <div
      className={`${util.modalOverlay} badge-details-modal-overlay`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="badge-modal-title"
      aria-describedby="badge-modal-description"
    >
      <div
        className={`${util.modalSurface} badge-details-modal`}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          className="badge-details-modal__close"
          onClick={onClose}
          aria-label="Close badge details modal"
        >
          ×
        </button>

        <div className="badge-details-modal__body">
          {isLoading ? (
            <p className="badge-modal-loading" aria-live="polite">
              Loading badge details…
            </p>
          ) : (
            <>
              <h2 id="badge-modal-title" className="badge-modal-title">
                {badge?.name}
              </h2>

              {badge?.description && (
                <p id="badge-modal-description" className="badge-modal-description">
                  {badge.description}
                </p>
              )}

              <div className="badge-modal-imageWrapper">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={`${badge?.name || 'Badge'} artwork`}
                    className="badge-modal-image"
                  />
                ) : (
                  <span className="badge-modal-placeholder" aria-hidden="true">
                    {getBadgeIcon(badge?.badge_type)}
                  </span>
                )}
              </div>

              <LinkedInShareButton
                badge={badge}
                digitalBadge={digitalBadge}
                onSharingSuccess={onLinkedInShare}
                onSharingError={(error) => console.error('LinkedIn sharing error:', error)}
                className={linkedinButtonClass}
                variant="primary"
                showInstructions={false}
              />

              <div className="badge-modal-socialRow">
                <button
                  type="button"
                  className={`${socialButtonClass} ${util['btn--xtwitter']}`}
                  onClick={() => handleSocialShare('twitter')}
                  disabled={!verificationUrl}
                  aria-label="Share badge on X"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="20" height="20" aria-hidden="true" focusable="false">
                    <path fill="currentColor" d="M12.6.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867-5.07-4.425 5.07H.316l5.733-6.57L0 .75h5.063l3.495 4.633L12.601.75Zm-.86 13.028h1.36L4.323 2.145H2.865z"/>
                  </svg>

                </button>
                <button
                  type="button"
                  className={`${socialButtonClass} ${util['btn--fb']}`}
                  onClick={() => handleSocialShare('facebook')}
                  disabled={!verificationUrl}
                  aria-label="Share badge on Facebook"
                  title={facebookShareTooltip}
                  aria-describedby={facebookTooltipVisible ? facebookTooltipId : undefined}
                  onMouseEnter={() => setFacebookTooltipVisible(true)}
                  onMouseLeave={() => setFacebookTooltipVisible(false)}
                  onFocus={() => setFacebookTooltipVisible(true)}
                  onBlur={() => setFacebookTooltipVisible(false)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="22" height="22" aria-hidden="true">
                    <path fill="currentColor" d="M16 8.049c0-4.446-3.582-8.05-8-8.05C3.58 0-.002 3.603-.002 8.05c0 4.017 2.926 7.347 6.75 7.951v-5.625h-2.03V8.05H6.75V6.275c0-2.017 1.195-3.131 3.022-3.131.876 0 1.791.157 1.791.157v1.98h-1.009c-.993 0-1.303.621-1.303 1.258v1.51h2.218l-.354 2.326H9.25V16c3.824-.604 6.75-3.934 6.75-7.951z"/>
                  </svg>

                </button>

                <button
                  type="button"
                  className={socialButtonClass}
                  onClick={() => handleSocialShare('email')}
                  disabled={!verificationUrl}
                  aria-label="Share badge via email"
                >
                  {/* Email icon: circle + outlined envelope */}
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 60" width="25" height="25">
                    <g id="_8-Email" data-name="8-Email">
                      <g id="_Group_" data-name="&lt;Group&gt;">
                        <path fill="#ffe299" d="m57 12 .24-.24A5.944 5.944 0 0 1 59 16v28a5.944 5.944 0 0 1-1.76 4.24L57 48 36 27l-.06-.08zM3 48l-.24.24A5.944 5.944 0 0 1 1 44V16a5.944 5.944 0 0 1 1.76-4.24L3 12l21.06 14.92L24 27z"/>
                        <path fill="#fff49c" d="m57 48 .24.24A5.944 5.944 0 0 1 53 50H7a5.944 5.944 0 0 1-4.24-1.76L3 48l21-21 .06-.08L27 29a4.38 4.38 0 0 0 3 1 4.38 4.38 0 0 0 3-1l2.94-2.08.06.08z"/>
                        <path fill="#fff48c" d="M2.76 11.76A5.944 5.944 0 0 1 7 10h46a5.944 5.944 0 0 1 4.24 1.76L57 12 35.94 26.92 33 29a4.38 4.38 0 0 1-3 1 4.38 4.38 0 0 1-3-1l-2.94-2.08L3 12z"/>
                        <path fill="#ffd287" d="M27 33a4.38 4.38 0 0 0 3 1 4.38 4.38 0 0 0 3-1l2.94-2.08 2.33-1.65L36 27l-.06-.08L33 29a4.38 4.38 0 0 1-3 1 4.38 4.38 0 0 1-3-1l-2.94-2.08L24 27l-2.27 2.27 2.33 1.65z"/>
                        <path d="M55 46H5l-2 2-.24.24A5.944 5.944 0 0 0 7 50h46a5.944 5.944 0 0 0 4.24-1.76L57 48z" fill="#ffda45"/>
                        <g opacity=".54">
                          <path d="M13.616 10H7a5.944 5.944 0 0 0-4.24 1.76L3 12l4.862 3.444-.1-.219C9.252 11.989 11.323 10 13.616 10z" fill="#fff"/>
                        </g>
                        <path d="M57.951 11.056A6.919 6.919 0 0 0 53 9H7a7.009 7.009 0 0 0-7 7v28a7.008 7.008 0 0 0 7 7h46a7.008 7.008 0 0 0 7-7V16a6.913 6.913 0 0 0-2.049-4.944zM53 11a4.945 4.945 0 0 1 2.617.754l-23.195 16.43a1.1 1.1 0 0 0-.118 .1A3.461 3.461 0 0 1 30 29a3.478 3.478 0 0 1-2.293-.707.954.954 0 0 0-.129-.109L4.383 11.754A4.937 4.937 0 0 1 7 11zM2 16a4.94 4.94 0 0 1 .9-2.847l19.63 13.905-19.7 19.7A4.947 4.947 0 0 1 2 44zm5 33a4.936 4.936 0 0 1-2.756-.83l19.94-19.939 2.185 1.547A5.432 5.432 0 0 0 30 31a5.432 5.432 0 0 0 3.631-1.222l2.185-1.547 19.941 19.94A4.947 4.947 0 0 1 53 49zm51-5a4.936 4.936 0 0 1-.83 2.756l-19.7-19.7 19.63-13.9A4.94 4.94 0 0 1 58 16z" fill="#000"/>
                      </g>
                    </g>
                  </svg>
                </button>

              {facebookTooltipVisible && (
                  <div
                    id={facebookTooltipId}
                    className="badge-modal-tooltip"
                    role="status"
                    aria-live="polite"
                  >
                    {facebookShareTooltip}
                  </div>
                )}
              </div>

              {earnedDate && (
                <p className="badge-modal-date">
                  <span className="badge-modal-dateLabel">Date Earned:</span> {formatDate(earnedDate)}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BadgeDetailsModal;
